class ProductTabsCustom extends HTMLElement {
  #controller = new AbortController();

  get #tablist() {
    return this.querySelector('[data-product-tabs-tablist]');
  }

  get #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  get #panels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]'));
  }

  connectedCallback() {
    this.#moveTabsIntoTablist();
    const { signal } = this.#controller;
    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', this.#onClick, { signal });
      tab.addEventListener('keydown', this.#onKeyDown, { signal });
    });
    this.querySelectorAll('.product-tabs__arrows .slideshow-control').forEach((arrow) => {
      arrow.addEventListener('click', this.#onArrowClick, { signal });
    });
    this.addEventListener('slideshow:select', this.#syncArrowStates, { signal });
    this.#syncViewAllLink();
    requestAnimationFrame(() => this.#syncArrowStates());
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #moveTabsIntoTablist() {
    const tablist = this.#tablist;
    if (!tablist) return;
    const buttons = this.querySelectorAll('[data-product-tab-button]');
    buttons.forEach((btn) => tablist.appendChild(btn));
  }

  #onClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    this.#activate(tab);
  };

  #onKeyDown = (event) => {
    const tabs = this.#tabs;
    const currentIndex = tabs.indexOf(/** @type {HTMLElement} */ (event.currentTarget));
    if (currentIndex < 0) return;

    let nextIndex = -1;
    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#activate(tabs[currentIndex]);
        return;
      default:
        return;
    }

    event.preventDefault();
    const target = tabs[nextIndex];
    if (!target) return;
    target.focus();
    this.#activate(target);
  };

  #activate(tab) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    const panelId = tab.getAttribute('aria-controls');

    tabs.forEach((t) => {
      const active = t === tab;
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.setAttribute('tabindex', active ? '0' : '-1');
      t.classList.toggle('product-tabs__tab--active', active);
    });

    panels.forEach((panel) => {
      const active = panel.id === panelId;
      panel.toggleAttribute('hidden', !active);
      if (active) {
        const slideshow = panel.querySelector('slideshow-component');
        if (slideshow && typeof slideshow.select === 'function') {
          slideshow.select(0, null, { instant: true });
        }
      }
    });

    this.#syncViewAllLink();
    window.dispatchEvent(new Event('resize'));
    requestAnimationFrame(() => requestAnimationFrame(() => this.#syncArrowStates()));
  }

  #syncArrowStates = () => {
    const activePanel = this.querySelector('[role="tabpanel"]:not([hidden])');
    if (!activePanel) return;
    const slideshow = activePanel.querySelector('slideshow-component');
    const prevArrow = this.querySelector('.product-tabs__arrows .slideshow-control--previous');
    const nextArrow = this.querySelector('.product-tabs__arrows .slideshow-control--next');
    if (!slideshow || !prevArrow || !nextArrow) return;
    const current = slideshow.current ?? 0;
    const nextIndex = slideshow.nextIndex ?? 0;
    const infinite = Boolean(slideshow.infinite);
    const total = slideshow.slides?.length ?? 0;
    prevArrow.toggleAttribute('disabled', !infinite && current <= 0);
    nextArrow.toggleAttribute('disabled', !infinite && total > 0 && nextIndex >= total);
  };

  #onArrowClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    let action = null;
    if (button.classList.contains('slideshow-control--previous')) action = 'previous';
    else if (button.classList.contains('slideshow-control--next')) action = 'next';
    if (!action) return;
    const activePanel = this.querySelector('[role="tabpanel"]:not([hidden])');
    if (!activePanel) return;
    const slideshow = activePanel.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow[action] !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    slideshow[action](event);
  };

  #syncViewAllLink() {
    // Two view-all links exist (desktop header + mobile "below" variant);
    // querySelector only reached the first, leaving the mobile one on "#".
    const links = this.querySelectorAll('[data-product-tabs-view-all]');
    if (!links.length) return;
    const activeTab = this.querySelector('[role="tab"][aria-selected="true"]');
    if (!(activeTab instanceof HTMLElement)) return;
    const url = activeTab.dataset.collectionUrl;
    if (!url) return;
    links.forEach((link) => {
      if (link instanceof HTMLAnchorElement) link.href = url;
    });
  }
}

if (!customElements.get('product-tabs-custom')) {
  customElements.define('product-tabs-custom', ProductTabsCustom);
}
